import { useState, useEffect, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Calendar, PoundSterling, MapPin, Clock, Filter, Music, Users, Star, Globe, FileText, Plus, Edit } from "lucide-react";
import { format } from "date-fns";
import type { ActWithParticipants, Booking, ExternalEvent, Venue } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { BookingFormModal } from "@/components/bookings/booking-form-modal";
import { ActFormModal } from "@/components/acts/act-form-modal";
import { VenueBookingModal } from "@/components/bookings/venue-booking-modal";
import { BookingTypeDialog } from "@/components/bookings/booking-type-dialog";

type BookingWithEvent = Booking & {
  externalEvent: ExternalEvent & { venue?: Venue };
  act: ActWithParticipants;
};

export default function ActSummary() {
  const { orgId, actId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("date-desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showVenueBookingModal, setShowVenueBookingModal] = useState(false);
  const [showBookingTypeDialog, setShowBookingTypeDialog] = useState(false);
  const [showEditActModal, setShowEditActModal] = useState(false);

  // Check permissions
  useEffect(() => {
    if (!orgId || !actId) {
      setLocation("/");
      return;
    }

    const organisationId = parseInt(orgId || "0");
    const hasAccess = user?.role === 'god' || 
      user?.organisationUsers?.some(ou => 
        ou.organisation.id === organisationId && 
        (ou.role === 'owner' || ou.role === 'operator')
      );

    if (!hasAccess) {
      setLocation("/");
    }
  }, [orgId, actId, user]); // Removed setLocation from dependencies

  // Fetch act details
  const { data: act, isLoading: isLoadingAct } = useQuery<ActWithParticipants>({
    queryKey: [`/api/acts/${actId}`],
    queryFn: async () => {
      const response = await fetch(`/api/acts/${actId}`);
      if (!response.ok) throw new Error("Failed to fetch act");
      return response.json();
    },
    enabled: !!actId,
  });

  // Fetch bookings for this act
  const { data: bookings = [], isLoading: isLoadingBookings } = useQuery<BookingWithEvent[]>({
    queryKey: [`/api/bookings?organisationId=${orgId}&actId=${actId}`],
    queryFn: async () => {
      const response = await fetch(`/api/bookings?organisationId=${orgId}&actId=${actId}`);
      if (!response.ok) throw new Error("Failed to fetch bookings");
      return response.json();
    },
    enabled: !!actId && !!orgId,
  });

  // All hooks must be called before any conditional returns
  const displayName = useMemo(() => 
    act?.name || act?.participants?.[0]?.participant.name || "Unknown Act",
    [act?.name, act?.participants]
  );

  // Filter and sort bookings
  const filteredBookings = useMemo(() => {
    return bookings
      .filter(booking => {
        // Status filter
        if (statusFilter !== "all" && booking.status !== statusFilter) return false;
        
        // Search filter
        if (searchQuery) {
          const search = searchQuery.toLowerCase();
          return (
            booking.externalEvent?.name?.toLowerCase().includes(search) ||
            booking.externalEvent?.venue?.name?.toLowerCase().includes(search) ||
            booking.notes?.toLowerCase().includes(search)
          );
        }
        
        return true;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case "date-asc":
            return new Date(a.externalEvent?.date || 0).getTime() - new Date(b.externalEvent?.date || 0).getTime();
          case "date-desc":
            return new Date(b.externalEvent?.date || 0).getTime() - new Date(a.externalEvent?.date || 0).getTime();
          case "fee-asc":
            return (a.fee || 0) - (b.fee || 0);
          case "fee-desc":
            return (b.fee || 0) - (a.fee || 0);
          case "status":
            return (a.status || "").localeCompare(b.status || "");
          default:
            return 0;
        }
      });
  }, [bookings, statusFilter, sortBy, searchQuery]);

  const calculateTotalRevenue = useMemo(() => {
    return filteredBookings
      .filter(b => b.status === 'completed' && b.fee)
      .reduce((sum, b) => sum + (b.fee || 0), 0) / 100;
  }, [filteredBookings]);

  const calculateTotalCommission = useMemo(() => {
    return filteredBookings
      .filter(b => b.status === 'completed' && b.commissionAmount)
      .reduce((sum, b) => sum + (b.commissionAmount || 0) / 100, 0);
  }, [filteredBookings]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "partial":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "refunded":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (isLoadingAct || isLoadingBookings) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading act details...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (!act) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <p className="text-slate-700">Act not found</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader backUrl={`/organisations/${orgId}/acts`} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">

          <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm flex items-center gap-3">
                  <Music className="h-8 w-8 text-purple-600" />
                  {displayName}
                </h1>
                {act.participants && act.participants.length > 0 && act.name && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {act.participants.map(ap => (
                      <Badge key={ap.participantId} variant="secondary">
                        {ap.participant.name}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                  {act.actType && (
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {act.actType.charAt(0).toUpperCase() + act.actType.slice(1)}
                    </span>
                  )}
                  {act.genre && act.genre.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Music className="h-4 w-4" />
                      {act.genre.join(", ")}
                    </span>
                  )}
                  {act.rating && (
                    <span className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      {act.rating}/5
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => setShowEditActModal(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Act
                </Button>
                {act.website && (
                  <a
                    href={act.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <Globe className="h-4 w-4" />
                    Website
                  </a>
                )}
                {act.bio && (
                  <div className="max-w-md">
                    <p className="text-sm text-slate-600">{act.bio}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Total Bookings</p>
                <p className="text-xl font-bold">{bookings.length}</p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Confirmed</p>
                <p className="text-xl font-bold text-green-600">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Total Revenue</p>
                <p className="text-xl font-bold">£{calculateTotalRevenue.toLocaleString()}</p>
              </div>
              <div className="bg-white/30 rounded-lg p-3">
                <p className="text-xs text-slate-600">Total Commission</p>
                <p className="text-xl font-bold">£{calculateTotalCommission.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="mb-6 bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search bookings..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/50"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white/50">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[180px] bg-white/50">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">Date (Newest)</SelectItem>
                  <SelectItem value="date-asc">Date (Oldest)</SelectItem>
                  <SelectItem value="fee-desc">Fee (Highest)</SelectItem>
                  <SelectItem value="fee-asc">Fee (Lowest)</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => setShowBookingTypeDialog(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Booking
              </Button>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <Card className="bg-white/20 backdrop-blur-xl border-white/30">
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  No bookings found
                </h3>
                <p className="text-slate-600">
                  {statusFilter !== "all" || searchQuery 
                    ? "Try adjusting your filters"
                    : "This act doesn't have any bookings yet"}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredBookings.map((booking) => (
              <Card 
                key={booking.id} 
                className="bg-white/20 backdrop-blur-xl border-white/30 hover:bg-white/30 transition-all duration-300 cursor-pointer"
                onClick={() => setLocation(`/organisations/${orgId}/bookings/${booking.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {booking.externalEvent?.name || "Unknown Event"}
                        </h3>
                        <div className="flex gap-2">
                          <Badge className={getStatusColor(booking.status)}>
                            {booking.status}
                          </Badge>
                          {booking.paymentStatus && booking.paymentStatus !== 'pending' && (
                            <Badge className={getPaymentStatusColor(booking.paymentStatus)}>
                              {booking.paymentStatus}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm text-slate-600">
                        {booking.externalEvent?.venue && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{booking.externalEvent.venue.name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {booking.externalEvent?.date && 
                              format(new Date(booking.externalEvent.date), "PPP")}
                          </span>
                        </div>
                        {booking.performanceTime && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>
                              {format(new Date(booking.performanceTime), "p")}
                              {booking.durationMinutes && ` (${booking.durationMinutes} min)`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {booking.fee && (
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end mb-1">
                          <PoundSterling className="h-4 w-4 text-slate-600" />
                          <span className="text-lg font-semibold">
                            £{(booking.fee / 100).toFixed(2)}
                          </span>
                        </div>
                        {booking.commissionAmount && (
                          <p className="text-xs text-slate-600">
                            Commission: {booking.commissionType === 'percentage' && booking.commissionValue
                              ? `${(booking.commissionValue / 100).toFixed(1)}%`
                              : 'Flat'
                            }
                            {' '}
                            (£{(booking.commissionAmount / 100).toFixed(2)})
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {booking.notes && (
                    <p className="mt-3 text-sm text-slate-600 italic">
                      {booking.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Booking Type Selection Dialog */}
      <BookingTypeDialog
        open={showBookingTypeDialog}
        onOpenChange={setShowBookingTypeDialog}
        onSelectType={(type) => {
          if (type === "event") {
            setShowBookingModal(true);
          } else {
            setShowVenueBookingModal(true);
          }
        }}
      />

      {/* Event Booking Form Modal */}
      <BookingFormModal
        open={showBookingModal}
        onOpenChange={setShowBookingModal}
        organisationId={parseInt(orgId || "0")}
        actId={parseInt(actId || "0")}
      />

      {/* Venue Booking Form Modal */}
      <VenueBookingModal
        open={showVenueBookingModal}
        onOpenChange={setShowVenueBookingModal}
        organisationId={parseInt(orgId || "0")}
        actId={parseInt(actId || "0")}
      />

      {/* Edit Act Modal */}
      <ActFormModal
        open={showEditActModal}
        onOpenChange={setShowEditActModal}
        organisationId={parseInt(orgId || "0")}
        act={act}
      />
    </PageBackground>
  );
}